import React, {useMemo, useRef, useState} from 'react';
import {ContextualMenu, Icon, IContextualMenuItem, Link, MessageBar, MessageBarType, Spinner, SpinnerSize} from '@fluentui/react';
import {useHistory} from 'react-router-dom';
import {useGetInstalledExtensionsQuery, useLazyGetDirectImportPreviewQuery, useRunDirectImportExtensionMutation} from '../../store/Api';
import {useLazyGetWorkspaceRecordLinksQuery} from '../../store/endpoints/RecordLinksEndpoints';
import {IInstalledExtension} from '../../models/Extension';
import OpportunityPickerDialog from '../GlobalSearch/OpportunityPickerDialog';

interface IOpportunityAttachControlProps {
    workspaceId: string;
    /** Hide the control once the workspace already carries an opportunity link. */
    hasOpportunity: boolean;
    /** Fired after a successful import so the page can refresh items, links, and the workspace. */
    onImported: () => void;
}

/**
 * The header's "Attach opportunity" pill. With one opportunity direct-import extension installed it
 * opens that PSA's opportunity picker straight away; with several it asks which PSA first. Picking an
 * opportunity runs the direct import into THIS workspace (items, bundles, company + contact, and the
 * opportunity link), the same import the quote-link quick flow runs into a fresh workspace.
 */
const OpportunityAttachControl: React.FC<IOpportunityAttachControlProps> = ({workspaceId, hasOpportunity, onImported}) => {
    const history = useHistory();
    const installed = useGetInstalledExtensionsQuery();
    const [getRecordLinks] = useLazyGetWorkspaceRecordLinksQuery();
    const [getPreview] = useLazyGetDirectImportPreviewQuery();
    const [runImport] = useRunDirectImportExtensionMutation();
    const pillRef = useRef<HTMLSpanElement>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [pickerExtension, setPickerExtension] = useState<IInstalledExtension>();
    const [status, setStatus] = useState<string>();
    const [error, setError] = useState<{message: string; workspaceId?: string}>();

    // Same test the omnibox uses for "Create prepared cart from opportunity".
    const extensions = useMemo(() =>
        (installed.data ?? []).filter(e => e.enabled
            && e.extensionType === 'directImport'
            && e.additionalData?.some(f => f.fieldType === 'directImportPreview')
            && `${e.id} ${e.name}`.toLowerCase().includes('opportunit')),
        [installed.data]);

    if (hasOpportunity || extensions.length === 0) return null;

    const start = () => {
        setError(undefined);
        if (extensions.length === 1) setPickerExtension(extensions[0]);
        else setMenuOpen(true);
    };

    const importOpportunity = async (extension: IInstalledExtension, opportunityId: string) => {
        setPickerExtension(undefined);
        setError(undefined);
        try {
            setStatus('Checking for an existing import…');
            const links = await getRecordLinks({field: 'opportunityId', externalIds: [opportunityId]}).unwrap().catch(() => []);
            const existing = links.find(l => l.externalId === opportunityId);
            if (existing && existing.workspaceId !== workspaceId) {
                setError({message: `That opportunity is already imported into "${existing.workspaceName ?? 'another workspace'}".`, workspaceId: existing.workspaceId});
                return;
            }

            setStatus('Loading the opportunity from the PSA…');
            const previewField = extension.additionalData?.find(f => f.fieldType === 'directImportPreview')?.field;
            if (!previewField) throw new Error('This extension does not support direct import.');
            const preview = await getPreview({
                extensionId: extension.id,
                fieldName: previewField,
                request: {sourceName: extension.source, objectId: opportunityId, page: 1, pageSize: 1000}
            }).unwrap();
            if (preview.success === false) throw new Error(preview.error ?? 'The PSA lookup failed.');
            const items = preview.items ?? [];
            if (items.length === 0) throw new Error('The opportunity has no products to import.');

            setStatus(`Importing ${items.length} item${items.length === 1 ? '' : 's'}…`);
            const result = await runImport({
                extensionId: extension.id,
                request: {
                    catalog: extension.targetCatalog ?? '',
                    workspaceId,
                    items,
                    importBundles: true,
                    attachContact: true
                }
            }).unwrap();
            if (result.success === false) throw new Error(result.error ?? 'The import failed.');
            onImported();
        } catch (e) {
            setError({message: e instanceof Error ? e.message : 'The opportunity import failed.'});
        } finally {
            setStatus(undefined);
        }
    };

    const menuItems: IContextualMenuItem[] = extensions.map(e => ({
        key: e.id,
        text: e.vendor ?? e.source ?? e.name,
        iconProps: {iconName: 'Trending12'},
        onClick: () => setPickerExtension(e)
    }));

    return (
        <>
            <span
                ref={pillRef}
                role='button'
                title={status ?? 'Import a PSA opportunity into this workspace'}
                aria-busy={!!status}
                onClick={status ? undefined : start}
                style={{
                    cursor: status ? 'default' : 'pointer',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: 'transparent',
                    color: '#5e35b1',
                    border: '1px dashed #b39ddb',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                }}
            >
                {status ? <Spinner size={SpinnerSize.xSmall} /> : <Icon iconName='Trending12' style={{fontSize: 10}} />}
                {status ?? 'Attach opportunity'}
            </span>
            {menuOpen &&
                <ContextualMenu
                    items={menuItems}
                    target={pillRef.current}
                    onDismiss={() => setMenuOpen(false)}
                    onItemClick={() => setMenuOpen(false)}
                />}
            {pickerExtension &&
                <OpportunityPickerDialog
                    extensionId={pickerExtension.id}
                    onDismiss={() => setPickerExtension(undefined)}
                    onPick={(opportunityId) => void importOpportunity(pickerExtension, opportunityId)}
                />}
            {error &&
                <MessageBar messageBarType={MessageBarType.warning} onDismiss={() => setError(undefined)} styles={{root: {marginTop: '0.5em'}}}>
                    {error.message}
                    {error.workspaceId && <> <Link onClick={() => history.push(`/Workspace/${error.workspaceId}`)}>Open it</Link>.</>}
                </MessageBar>}
        </>
    );
};

export default OpportunityAttachControl;
